import React, { useRef, useState } from "react";
import { Download, Upload, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CHUNK = 5 * 1024 * 1024;
const rid = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function Report({ r }) {
  return (
    <div className="mt-4 space-y-2 text-sm" data-testid="ea-curve-import-report">
      {r.points.map((p) => (
        <div key={`${p.pod}-${p.year}`} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`ea-curve-import-point-${p.pod}`}>
          <span className="font-semibold">{p.pod}</span> · {p.year} · {p.days_15min} giorni a 15 min · {p.days_hourly} giorni orari
          {p.hourly_skipped_existing_15min ? ` · ${p.hourly_skipped_existing_15min} giorni orari ignorati (curva 15 min già presente)` : ""} · mesi {p.months.map((m) => MONTHS[m - 1]).join(", ") || "—"}
          {p.warnings.map((w) => <div key={w} className="text-xs text-amber-700">{w}</div>)}
        </div>
      ))}
      {r.zero_days_skipped > 0 && <div className="text-xs text-slate-500">{r.zero_days_skipped} giorni vuoti o a zero ignorati (dati mancanti).</div>}
      {r.unknown_pods.length > 0 && <div className="text-xs text-red-600" data-testid="ea-curve-import-unknown">POD non presenti per l'anno (importa prima il controllo fatture): {r.unknown_pods.join(", ")}</div>}
      {r.row_errors.map((e) => <div key={e} className="text-xs text-red-600">{e}</div>)}
    </div>
  );
}

export default function EaCurveImport({ company, askConfirm }) {
  const cid = company?.id;
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);
  const ref = useRef(null);
  const template = async () => {
    try {
      const r = await api.get("/ea/curves/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: "Template_curve_consumo_EA.xlsx" }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template non disponibile"); }
  };
  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!cid) return toast.error("Seleziona prima il cliente in alto");
    askConfirm(`le curve di consumo (${f.name})`, () => doUpload(f));
  };
  const doUpload = async (f) => {
    const id = rid(), total = Math.max(1, Math.ceil(f.size / CHUNK));
    setReport(null);
    try {
      let last;
      for (let i = 0; i < total; i++) {
        setBusy(i < total - 1 ? `Caricamento ${Math.round(((i + 1) / total) * 100)}%` : "Elaborazione curve…");
        const fd = new FormData();
        Object.entries({ upload_id: id, index: String(i), total: String(total), filename: f.name, company_id: cid }).forEach(([k, v]) => fd.append(k, v));
        fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK), f.name);
        last = (await api.post("/ea/curves/import/chunk", fd, { timeout: 300000 })).data;
      }
      setReport(last.report);
      toast.success("Curve importate");
    } catch (err) { toast.error(err.response?.data?.detail || "Import non riuscito"); }
    setBusy(null);
  };
  return (
    <div className="card-elevated p-5" data-testid="ea-curve-import">
      <div className="flex items-center gap-2 font-display font-semibold text-slate-900"><Activity size={18} /> Import curve di consumo (15 minuti e orarie)</div>
      <p className="text-sm text-slate-500 mt-1 max-w-3xl">Carica curve a 15 minuti e/o orarie per i POD Power anche senza il file di controllo fatture, con il template dedicato (fogli Curva_15min e Curva_oraria). I POD devono essere già presenti per l'anno. La curva importata sostituisce quella esistente negli stessi giorni; la curva oraria non sostituisce una curva a 15 minuti già presente. Giorni vuoti o a zero = dati mancanti.</p>
      <div className="flex flex-wrap items-end gap-3 mt-4">

        <button onClick={template} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2" data-testid="ea-curve-template"><Download size={14} /> Scarica template</button>
        <button onClick={() => ref.current?.click()} disabled={!!busy || !cid} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="ea-curve-upload"><Upload size={15} /> {busy || "Importa curve"}</button>
        <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={upload} data-testid="ea-curve-upload-input" />
      </div>
      {report && <Report r={report} />}
    </div>
  );
}
